import { Icon } from "@mdi/react";
import {
  mdiAlertCircleOutline,
  mdiCheck,
  mdiCheckAll,
  mdiClockOutline,
  mdiLoading,
} from "@mdi/js";
import { useTranslation } from "react-i18next";
import type { MessageStatusName, PlatformTypeName } from "@multi-chat/shared";
import { SimpleTooltip, TooltipProvider } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import { messageStatusPresentationKind } from "./message-status-presentation.js";

/** Compact glyphs for PENDING / SENDING / FAILED. */
const UTILITY_ICON_SIZE = "12px";
/** Single delivery check (SENT). */
const SENT_ICON_SIZE = "14px";
/** Purpose-built double-check (DELIVERED / READ). */
const DOUBLE_CHECK_ICON_SIZE = "16px";

const ICON_CLASS = "block shrink-0";

interface MessageStatusIndicatorProps {
  status: MessageStatusName;
  /** Conversation platform — gates WhatsApp tick semantics. */
  platformType: PlatformTypeName;
  /** Light glyphs/text for dark media overlays. */
  presentation?: "default" | "onMedia";
  /** Sanitized outbound failure code for FAILED messages. */
  failureCode?: string;
}

function statusIcon(status: Exclude<MessageStatusName, "RECEIVED">): React.JSX.Element {
  switch (status) {
    case "PENDING":
      return (
        <Icon
          path={mdiClockOutline}
          size={UTILITY_ICON_SIZE}
          className={ICON_CLASS}
          aria-hidden
        />
      );
    case "SENDING":
      return (
        <Icon
          path={mdiLoading}
          size={UTILITY_ICON_SIZE}
          spin
          className={ICON_CLASS}
          aria-hidden
        />
      );
    case "SENT":
      return (
        <Icon path={mdiCheck} size={SENT_ICON_SIZE} className={ICON_CLASS} aria-hidden />
      );
    case "DELIVERED":
    case "READ":
      return (
        <Icon
          path={mdiCheckAll}
          size={DOUBLE_CHECK_ICON_SIZE}
          className={ICON_CLASS}
          aria-hidden
        />
      );
    case "FAILED":
      return (
        <Icon
          path={mdiAlertCircleOutline}
          size={UTILITY_ICON_SIZE}
          className={ICON_CLASS}
          aria-hidden
        />
      );
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function statusToneClass(status: MessageStatusName): string | undefined {
  if (status === "READ") return "text-message-status-read";
  if (status === "FAILED") return "text-error";
  return undefined;
}

export function MessageStatusIndicator({
  status,
  platformType,
  presentation = "default",
  failureCode,
}: MessageStatusIndicatorProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const kind = messageStatusPresentationKind(platformType, status);

  if (kind == null || status === "RECEIVED") {
    return null;
  }

  const label =
    status === "FAILED" && failureCode === "VOICE_NORMALIZATION_FAILED"
      ? t("inbox.status.voiceNormalizationFailed")
      : (platformType === "FACEBOOK" || platformType === "INSTAGRAM") && status === "READ"
        ? t("inbox.status.facebookSeen")
        : t(`inbox.status.${status}`);

  const onMedia = presentation === "onMedia";

  return (
    <TooltipProvider delayDuration={400}>
      <SimpleTooltip content={label}>
        <span
          className={cn(
            "inline-flex shrink-0 cursor-default items-center justify-center leading-[0] [direction:ltr] [unicode-bidi:isolate] focus-visible:rounded-sm focus-visible:shadow-[0_0_0_2px_var(--color-ring)]",
            kind === "plain_label"
              ? cn(
                  "h-auto max-w-[4.5rem] truncate text-[0.65rem] font-medium tabular-nums",
                  onMedia ? "text-surface-elevated/90" : "text-ink-subtle",
                )
              : cn(
                  "h-[0.875rem] w-[1.05rem]",
                  onMedia ? "text-surface-elevated/95" : "text-ink-subtle",
                ),
            status === "READ" && (onMedia ? "text-sky-200" : undefined),
            statusToneClass(status) && !onMedia ? statusToneClass(status) : undefined,
            status === "FAILED" && onMedia ? "text-red-200" : undefined,
          )}
          tabIndex={0}
          role="img"
          aria-label={label}
        >
          {kind === "plain_label" ? label : statusIcon(status)}
        </span>
      </SimpleTooltip>
    </TooltipProvider>
  );
}
